import {FC, useState, useEffect} from 'react'
import {PageTitle} from '../../../../../_metronic/layout/core'
import {Button, Col, Row, Spinner, Table, Badge, Form, Modal} from 'react-bootstrap'
import {panaAdmin, aktivitasPenugasan} from '../../../../../services/pana'
import {toast} from 'react-toastify'
import type {AktivitasPenugasan as AktivitasType} from '../../../../../types/pana'

const AktivitasPenugasanAdmin: FC = () => {
  const [data, setData] = useState<any>({})
  const [loading, setLoading] = useState<boolean>(false)
  const [showModal, setShowModal] = useState<boolean>(false)
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [isEdit, setIsEdit] = useState<boolean>(false)
  const [selectedAktivitas, setSelectedAktivitas] = useState<AktivitasType | null>(null)

  const [formData, setFormData] = useState({
    minggu_ke: 1,
    aktivitas: '',
    strategi: '',
  })

  const [pagination, setPagination] = useState({
    per_page: 10,
    page: 1,
  })

  const [query, setQuery] = useState<any>({
    sort_field: 'minggu_ke',
    sort_order: 'asc',
    minggu_ke: '',
  })

  const fetchData = async () => {
    setLoading(true)
    try {
      const response = await aktivitasPenugasan.getList({
        page: pagination.page,
        per_page: pagination.per_page,
        ...query,
      })

      // Parse Laravel pagination response (direct properties, not nested in meta)
      const responseData = response.data
      
      setData({
        data: responseData.data || [],
        total: responseData.total || 0,
        current_page: responseData.current_page || pagination.page,
        per_page: responseData.per_page || pagination.per_page,
      })
    } catch (error: any) {
      console.error('Error fetching aktivitas:', error)
      toast.error('Gagal memuat data aktivitas penugasan')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.per_page, pagination.page, query])

  const openCreateModal = () => {
    setIsEdit(false)
    setSelectedAktivitas(null)
    setFormData({
      minggu_ke: 1,
      aktivitas: '',
      strategi: '',
    })
    setShowModal(true)
  }

  const openEditModal = (aktivitas: AktivitasType) => {
    setIsEdit(true)
    setSelectedAktivitas(aktivitas)
    setFormData({
      minggu_ke: aktivitas.minggu_ke,
      aktivitas: aktivitas.aktivitas,
      strategi: aktivitas.strategi,
    })
    setShowModal(true)
  }

  const openDeleteModal = (aktivitas: AktivitasType) => {
    setSelectedAktivitas(aktivitas)
    setShowDeleteModal(true)
  }

  const handleSubmit = async () => {
    if (!formData.aktivitas || !formData.strategi) {
      toast.warning('Aktivitas dan Strategi harus diisi')
      return
    }

    setLoading(true)
    try {
      if (isEdit && selectedAktivitas) {
        await panaAdmin.updateAktivitas(selectedAktivitas.id, formData)
        toast.success('Aktivitas berhasil diperbarui')
      } else {
        await panaAdmin.createAktivitas(formData)
        toast.success('Aktivitas berhasil ditambahkan')
      }

      setShowModal(false)
      fetchData()
    } catch (error: any) {
      console.error('Error saving aktivitas:', error)
      toast.error('Gagal menyimpan aktivitas')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedAktivitas) return

    setLoading(true)
    try {
      await panaAdmin.deleteAktivitas(selectedAktivitas.id)
      toast.success('Aktivitas berhasil dihapus')
      setShowDeleteModal(false)
      setSelectedAktivitas(null)
      fetchData()
    } catch (error: any) {
      console.error('Error deleting:', error)
      toast.error('Gagal menghapus aktivitas')
    } finally {
      setLoading(false)
    }
  }

  const handleToggleStatus = async (aktivitas: AktivitasType) => {
    setLoading(true)
    try {
      await panaAdmin.toggleAktivitasStatus(aktivitas.id)
      toast.success(
        `Aktivitas ${aktivitas.is_active === 1 ? 'dinonaktifkan' : 'diaktifkan'}`
      )
      fetchData()
    } catch (error: any) {
      console.error('Error toggle status:', error)
      toast.error('Gagal mengubah status')
    } finally {
      setLoading(false)
    }
  }

  const onChangePerPage = (per_page: number) => {
    setPagination({
      ...pagination,
      per_page: per_page,
      page: 1,
    })
  }

  const getStatusBadge = (isActive: number) => {
    return isActive === 1 ? (
      <Badge bg='success'>Aktif</Badge>
    ) : (
      <Badge bg='secondary'>Nonaktif</Badge>
    )
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>Aktivitas Penugasan - Admin</PageTitle>

      <div className='card'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>
              Kelola Aktivitas Penugasan PANA
            </span>
            <span className='text-muted mt-1 fw-semibold fs-7'>
              Total: {data?.total || 0} aktivitas
            </span>
          </h3>
          <div className='card-toolbar'>
            <Button variant='primary' onClick={openCreateModal}>
              <i className='bi bi-plus-circle'></i> Tambah Aktivitas
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className='card-body py-3'>
          <Row className='mb-4'>
            <Col xs={12} md={6} lg={3} className='mb-3 mb-lg-0'>
              <Form.Group>
                <Form.Label>Filter Minggu</Form.Label>
                <Form.Select
                  value={query.minggu_ke}
                  onChange={(e) => setQuery({...query, minggu_ke: e.target.value})}
                >
                  <option value=''>Semua Minggu</option>
                  {Array.from({length: 12}, (_, i) => i + 1).map((minggu) => (
                    <option key={minggu} value={minggu}>
                      Minggu {minggu}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={6} md={3} lg={2} className='mb-3 mb-lg-0'>
              <Form.Group>
                <Form.Label>Urutan</Form.Label>
                <Form.Select
                  value={query.sort_order}
                  onChange={(e) => setQuery({...query, sort_order: e.target.value})}
                >
                  <option value='asc'>Ascending</option>
                  <option value='desc'>Descending</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={6} md={3} lg={2}>
              <Form.Group>
                <Form.Label>Per Page</Form.Label>
                <Form.Select
                  value={pagination.per_page}
                  onChange={(e) => onChangePerPage(Number(e.target.value))}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* Table */}
          <div className='table-responsive'>
            <Table hover>
              <thead>
                <tr className='fw-bold text-muted'>
                  <th className='min-w-50px'>Minggu</th>
                  <th className='min-w-250px'>Aktivitas</th>
                  <th className='min-w-250px'>Strategi</th>
                  <th className='min-w-80px'>Status</th>
                  <th className='min-w-100px'>Dibuat Oleh</th>
                  <th className='min-w-150px text-end'>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className='text-center py-5'>
                      <Spinner animation='border' />
                    </td>
                  </tr>
                ) : data?.data && data.data.length > 0 ? (
                  data.data.map((aktivitas: AktivitasType) => (
                    <tr key={aktivitas.id}>
                      <td>
                        <span className='badge badge-light-primary fs-6'>
                          Minggu {aktivitas.minggu_ke}
                        </span>
                      </td>
                      <td>
                        <span className='fw-semibold'>{aktivitas.aktivitas}</span>
                      </td>
                      <td>
                        <span className='text-muted'>{aktivitas.strategi}</span>
                      </td>
                      <td>{getStatusBadge(aktivitas.is_active)}</td>
                      <td>
                        <div className='d-flex flex-column'>
                          <span className='fw-bold'>
                            {typeof aktivitas.created_by === 'object'
                              ? aktivitas.created_by.fullname
                              : 'N/A'}
                          </span>
                          <span className='text-muted fs-7'>
                            {new Date(aktivitas.created_at).toLocaleDateString('id-ID')}
                          </span>
                        </div>
                      </td>
                      <td className='text-end'>
                        <div className='d-flex justify-content-end gap-2'>
                          <Button
                            size='sm'
                            variant={aktivitas.is_active === 1 ? 'warning' : 'success'}
                            onClick={() => handleToggleStatus(aktivitas)}
                            disabled={loading}
                            title={aktivitas.is_active === 1 ? 'Nonaktifkan' : 'Aktifkan'}
                          >
                            <i
                              className={`bi bi-${
                                aktivitas.is_active === 1 ? 'pause-circle' : 'play-circle'
                              }`}
                            ></i>
                          </Button>
                          <Button
                            size='sm'
                            variant='primary'
                            onClick={() => openEditModal(aktivitas)}
                            title='Edit'
                          >
                            <i className='bi bi-pencil'></i>
                          </Button>
                          <Button
                            size='sm'
                            variant='danger'
                            onClick={() => openDeleteModal(aktivitas)}
                            title='Hapus'
                          >
                            <i className='bi bi-trash'></i>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className='text-center py-5 text-muted'>
                      Tidak ada data
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {/* Pagination */}
          {!loading && data?.data && data.data.length > 0 && (
            <div className='d-flex flex-column flex-md-row justify-content-between align-items-center mt-4 gap-3'>
              <div className='text-muted small'>
                Menampilkan {((data.current_page - 1) * data.per_page) + 1} - {Math.min(data.current_page * data.per_page, data.total)} dari {data.total} data
              </div>
              <div className='d-flex gap-2 flex-wrap justify-content-center'>
                <Button
                  variant='light'
                  size='sm'
                  disabled={data.current_page === 1}
                  onClick={() => setPagination({...pagination, page: 1})}
                >
                  <i className='bi bi-chevron-double-left'></i>
                </Button>
                <Button
                  variant='light'
                  size='sm'
                  disabled={data.current_page === 1}
                  onClick={() => setPagination({...pagination, page: data.current_page - 1})}
                >
                  <i className='bi bi-chevron-left'></i>
                </Button>
                
                {/* Page Numbers */}
                {Array.from({length: Math.ceil(data.total / data.per_page)}, (_, i) => i + 1)
                  .filter((page) => {
                    const totalPages = Math.ceil(data.total / data.per_page)
                    if (totalPages <= 7) return true
                    if (page === 1 || page === totalPages) return true
                    if (page >= data.current_page - 1 && page <= data.current_page + 1) return true
                    return false
                  })
                  .map((page, index, array) => {
                    if (index > 0 && page - array[index - 1] > 1) {
                      return [
                        <span key={`ellipsis-${page}`} className='px-2'>...</span>,
                        <Button
                          key={page}
                          variant={data.current_page === page ? 'primary' : 'light'}
                          size='sm'
                          onClick={() => setPagination({...pagination, page})}
                        >
                          {page}
                        </Button>
                      ]
                    }
                    return (
                      <Button
                        key={page}
                        variant={data.current_page === page ? 'primary' : 'light'}
                        size='sm'
                        onClick={() => setPagination({...pagination, page})}
                      >
                        {page}
                      </Button>
                    )
                  })}

                <Button
                  variant='light'
                  size='sm'
                  disabled={data.current_page >= Math.ceil(data.total / data.per_page)}
                  onClick={() => setPagination({...pagination, page: data.current_page + 1})}
                >
                  <i className='bi bi-chevron-right'></i>
                </Button>
                <Button
                  variant='light'
                  size='sm'
                  disabled={data.current_page >= Math.ceil(data.total / data.per_page)}
                  onClick={() => setPagination({...pagination, page: Math.ceil(data.total / data.per_page)})}
                >
                  <i className='bi bi-chevron-double-right'></i>
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{isEdit ? 'Edit Aktivitas' : 'Tambah Aktivitas Baru'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className='mb-3'>
              <Form.Label>Minggu Ke *</Form.Label>
              <Form.Select
                value={formData.minggu_ke}
                onChange={(e) => setFormData({...formData, minggu_ke: Number(e.target.value)})}
              >
                {Array.from({length: 12}, (_, i) => i + 1).map((minggu) => (
                  <option key={minggu} value={minggu}>
                    Minggu {minggu}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label>Aktivitas *</Form.Label>
              <Form.Control
                as='textarea'
                rows={3}
                placeholder='Masukkan aktivitas yang harus dilakukan...'
                value={formData.aktivitas}
                onChange={(e) => setFormData({...formData, aktivitas: e.target.value})}
              />
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label>Strategi *</Form.Label>
              <Form.Control
                as='textarea'
                rows={3}
                placeholder='Masukkan strategi pelaksanaan...'
                value={formData.strategi}
                onChange={(e) => setFormData({...formData, strategi: e.target.value})}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowModal(false)}>
            Batal
          </Button>
          <Button
            variant='primary'
            onClick={handleSubmit}
            disabled={loading || !formData.aktivitas || !formData.strategi}
          >
            {loading ? <Spinner animation='border' size='sm' /> : isEdit ? 'Update' : 'Simpan'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Konfirmasi Hapus</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedAktivitas && (
            <>
              <div className='alert alert-warning'>
                <i className='bi bi-exclamation-triangle'></i> Apakah Anda yakin ingin menghapus
                aktivitas ini?
              </div>
              <div className='mb-2'>
                <strong>Minggu:</strong> {selectedAktivitas.minggu_ke}
              </div>
              <div className='mb-2'>
                <strong>Aktivitas:</strong> {selectedAktivitas.aktivitas}
              </div>
              <small className='text-danger'>
                Data yang dihapus tidak dapat dikembalikan. Ini akan mempengaruhi semua user
                PANA.
              </small>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowDeleteModal(false)}>
            Batal
          </Button>
          <Button variant='danger' onClick={handleDelete} disabled={loading}>
            {loading ? <Spinner animation='border' size='sm' /> : 'Hapus'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default AktivitasPenugasanAdmin
